import { parse } from 'date-fns';

import { updateAbn, updateNoAbn } from '../../slices/abn';
import { addGroup, Group } from '../../slices/appDetails';
import {
  updateAddressLine1,
  updateAnnualTurnover,
  updateCoverStart,
  updateDutyOfDisclosure,
  updateEmployeeCount,
  updateOccupationSearchType,
  updatePostcode,
  updateShowAllOccupationCategories,
  updateState,
  updateSuburb,
} from '../../slices/businessDetails';
import {
  updateBusinessName,
  updateEmail,
  updateFirstName,
  updateLastName,
  updateMobile,
  updatePartnerMembershipNumber,
} from '../../slices/extraInfo';
import {
  updateAccessToken,
  updateLevelOfExcess,
  updatePremiums,
  updateQuoteNumber,
} from '../../slices/quote';
import { AppDispatch } from '../../store';
import { TaxIndicator } from '../../types/enums';
import { PolicyResponse } from '../../types/policy';
import { Occupation, OccupationSearchType } from '../../types/refData';
import { RiskClassCode } from '../../types/risk';
import { DateFormat } from '../../utils';
import { DataIntegrityError, RiskNotFoundError } from './errors';
import { getRiskByClassCode } from './getRiskByClassCode';
import { updateCoversAction } from './updateCoversAction';
import { updateQuestionsAction } from './updateQuestionsAction';

export const getDateFromString = (str: string) => str.match(/\d\d\d\d-\d\d-\d\d/g)?.[0];

const updatePolicyHeaderData = ({
  dispatch,
  policyData,
}: {
  policyData: PolicyResponse;
  dispatch: AppDispatch;
}) => {
  const { policyTransactionInformation } = policyData.policy.policyHeader;
  const { inceptionDate, policyNumber: quoteNumber } = policyTransactionInformation;
  if (!inceptionDate) throw new DataIntegrityError('inception date not found in quote');
  const effectiveDate = getDateFromString(inceptionDate);

  if (!effectiveDate) throw new DataIntegrityError('inception date not valid');
  const parsedDate = parse(effectiveDate, DateFormat.yyyyMMdd, new Date());
  dispatch(updateCoverStart(JSON.stringify(parsedDate)));
  if (quoteNumber) dispatch(updateQuoteNumber(quoteNumber));
};

export const populateQuote = ({
  data,
  occupation,
  dispatch,
}: {
  data: PolicyResponse;
  occupation: Occupation | undefined;
  dispatch: AppDispatch;
}) => {
  const { policy } = data;
  const { risks, questions, customerParty } = policy;
  const {
    accessToken,
    firstName,
    lastName,
    orgABN,
    businessName,
    taxIndicator,
    contact: { primaryEmailAddress, mobilePhoneNumber } = {},
    clientIdMemberNo,
    intermediaryCode,
  } = customerParty;
  updatePolicyHeaderData({
    dispatch,
    policyData: data,
  });

  const isQuoteTillPayments = taxIndicator !== TaxIndicator.InValid;

  const liabilityRisk = getRiskByClassCode(risks, RiskClassCode.Liability);

  if (!liabilityRisk) {
    throw new RiskNotFoundError(RiskClassCode.Liability);
  }

  const liabilityDetails = liabilityRisk.riskDetail.riskBPKLiability;
  const { addressLine1: _addressLine1, city, stateCode, postalCode } = liabilityRisk.location;

  /** @note bootstrap business details slice */
  dispatch(updateDutyOfDisclosure(true));
  dispatch(updateShowAllOccupationCategories(true));
  dispatch(updateOccupationSearchType(OccupationSearchType.QuickSearch));

  dispatch(updateEmployeeCount(liabilityDetails.numberOfEmployees));
  dispatch(updateAnnualTurnover(liabilityDetails.annualTurnover));
  dispatch(updateAddressLine1(_addressLine1));
  dispatch(updateSuburb(city));
  dispatch(updateState(stateCode));
  dispatch(updatePostcode(postalCode));

  updateCoversAction({
    risks,
    occupation,
    dispatch,
  });

  /** @note bootstrap extra info slice */
  dispatch(updateFirstName(firstName!));
  dispatch(updateLastName(lastName!));
  dispatch(updateEmail(primaryEmailAddress!));
  dispatch(updateMobile(mobilePhoneNumber!));

  if (questions) {
    updateQuestionsAction({
      questions,
      isQuoteTillPayments,
      dispatch,
    });
  }

  if (orgABN) dispatch(updateAbn(orgABN));
  else if (isQuoteTillPayments) dispatch(updateNoAbn(true));
  dispatch(updateBusinessName(businessName!));

  /** @note bootstrap quote slice */
  dispatch(updateLevelOfExcess(liabilityDetails.liabilityExcess!));
  if (accessToken) dispatch(updateAccessToken(accessToken));
  dispatch(updatePremiums(data));

  dispatch(updatePartnerMembershipNumber(clientIdMemberNo));

  if (intermediaryCode && intermediaryCode !== 'staff') {
    dispatch(addGroup(intermediaryCode.toLowerCase() as Group));
  }

  return true;
};
